import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from "@material-ui/core/styles";
import { useSetState } from "Helpers/Hooks";
import DialogContent from "@material-ui/core/DialogContent";
import Input from "Components/Input/Input";
import Grid from "@material-ui/core/Grid";
import Translator from "Languages/Utility/Translator";
import { read } from "Helpers/LocalStorage";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { DialogActions } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import * as Icons from "react-bootstrap-icons";
import MuiAutoComplete from "Components/MuiAutoComplete/MuiAutoComplete";
import MuiCheckBoxGroup from "Components/MuiCheckboxGroup/MuiCheckboxGroup";
import AccountService from "Services/http/endpoints/superadmin/AccountsEndpoints";
import StatusHandler from "Helpers/StatusHandler";
import * as R from "ramda";
import { useDispatch, useSelector } from "react-redux";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import { getAccountingPriceServiceAgain } from "Stores/actions";

export default function EditPriceServices({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formValid, setFormValid] = useState({});
  const [loading, setLoading] = useState(false);
  const [checkValid, setCheckValid] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { activePeriods, serviceCombo } = useSelector((state) => ({
    activePeriods: state.saAccounts.activePeriods,
    serviceCombo: state.saAccounts.serviceCombo,
  }));
  const [controls, setControls] = useSetState({
    periods: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    services: {
      type: "select",
      selectItems: [],
      value: "",
      validation: null,
    },
    price: {
      type: "price",
      value: "",
      validation: {
        required: true,
        isPrice: true,
        min: 0,
      },
    },
    discount: {
      type: "number",
      value: 0,
      validation: {
        isNumeric: true,
        min: 0,
      },
    },
    maxDiscount: {
      type: "number",
      value: 0,
      validation: {
        isNumeric: true,
        min: 0,
      },
    },
    discountModel: {
      type: "checkbox",
      items: [
        { value: false, key: "percentageDiscount" },
        { value: false, key: "stepDiscount" },
      ],
    },
  });

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = () => {
    if (row && activePeriods && serviceCombo) {
      // fill selects
      const _periods = activePeriods.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      const _selectedPeriod = _periods.find((items) => {
        if (items.value === row.PeriodId) return items;
      });
      setControls({
        periods: {
          selectItems: _periods,
          value: _selectedPeriod,
        },
      });

      const _services = serviceCombo.map((child) => {
        return {
          label: child.Name,
          value: child.Id,
        };
      });
      const _selectedService = _services.find((items) => {
        if (items.value === row.ServiceId) return items;
      });
      setControls({
        services: {
          selectItems: _services,
          value: _selectedService,
        },
      });

      // set variables
      setControls({ price: { value: row.PriceUnit } });
      setControls({ discount: { value: row.ValueDiscount } });
      setControls({ maxDiscount: { value: row.MaxValueDiscount } });
      controls.discountModel.items[0].value = row.IsPercentDiscount;
      controls.discountModel.items[1].value = row.IsDiscountForeachCounter;
      setFormValid({ price: true });
    } else {
      showNotify({ text: t("getDataError"), customClass: "error" });
      onExit();
    }
  };

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
  };

  const autoCompleteHandler = (e, value) => {
    const controlName = e.target.id.split("-")[0];
    setControls({ [controlName]: { value: value } });
  };

  const checkboxHandler = (e, name, index) => {
    const items = controls[name].items;
    items[index] = { ...items[index], value: e.currentTarget.checked };
    setControls({ [name]: { items: items } });
  };

  const onFinish = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { periods, services, price, discount, maxDiscount, discountModel } =
        controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        const ValueDiscount = discount.value === "" ? 0 : +discount.value;
        const MaxValueDiscount =
          maxDiscount.value === "" ? 0 : +maxDiscount.value;
        const data = {
          Id: row.Id,
          KeyGroup: read("groupAKey"),
          KeyUser: read("userAKey"),
          PeriodId: periods.value.value,
          ServiceId: services.value.value,
          PriceUnit: +price.value,
          IsPercentDiscount: discountModel.items[0].value,
          IsDiscountForeachCounter: discountModel.items[1].value,
          ValueDiscount,
          MaxValueDiscount,
        };
        const response = await AccountService.putAccountingPriceService(data);
        // console.log(response);
        let err = StatusHandler(response);
        if (!err.isFail) {
          if (response.data.Value.IsError === true) {
            showNotify({
              text: response.data.Value.Describ,
              customClass: "error",
            });
          } else {
            showNotify({
              text: t("mission_accomplished"),
              customClass: "success",
            });
            await dispatch(getAccountingPriceServiceAgain());
            onExit();
          }
          setTimeout(() => {
            setLoading(false);
          }, 500);
        } else {
          setLoading(false);
        }
      } else {
        setCheckValid(true);
        setLoading(false);
      }
    } catch (err) {
      console.log("[EditPriceServices]", err);
      setLoading(false);
    }
  };

  const onExit = () => {
    setShowDialog(false);
    setLoading(false);
    setCheckValid(false);
  };

  return (
    <>
      <ActionFormatter
        title={<Translator id="editPriceService" />}
        icon={<Icons.Pencil />}
        onClick={onShowDialog}
        className="primary-button"
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="sm"
      >
        <BackDropLoading loading={loading} />
        <div className="dialogHeader">
          <p>{<Translator id={"editPriceService"} />}</p>
        </div>
        <DialogContent>
          <Grid container spacing={2}>
            {Object.keys(controls).map((key) => {
              return (
                <Grid
                  item
                  sm={controls[key].type === "checkbox" ? 12 : controls[key].type === "select" ? 6 : 4}
                  xs={12}
                  key={key}
                >
                  {controls[key].type === "select" && (
                    <MuiAutoComplete
                      autoSelect={true}
                      options={controls[key].selectItems}
                      label={<Translator id={key} />}
                      value={controls[key].value}
                      onChange={autoCompleteHandler}
                      id={key}
                    />
                  )}
                  {(controls[key].type === "price" ||
                    controls[key].type === "number") && (
                    <Input
                      setValue={setValue.bind(this)}
                      value={controls[key].value}
                      name={key}
                      validation={controls[key].validation}
                      label={<Translator id={key} />}
                      placeholder={t(key)}
                      type={controls[key].type}
                      checkValid={checkValid}
                      endIcon={
                        controls[key].type === "price" && (
                          <Translator id="moneyUnit" />
                        )
                      }
                    />
                  )}
                  {controls[key].type === "checkbox" && (
                    <MuiCheckBoxGroup
                      translate={true}
                      name={key}
                      value={controls[key].value}
                      items={controls[key].items}
                      onChange={checkboxHandler}
                      className="horizontalCheckbox"
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton
            color="primary"
            title={<Translator id="button_save" />}
            onClick={onFinish}
          />
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
